<template>
  <Modal
    :trigger="MODAL_CONFIG.trigger"
    :content-classes="MODAL_CONFIG.contentClasses"
    @update-modal-state="handleModalStateChange"
  >
    <template #content>
      <div
        ref="modalVotingContainerRef"
        :class="[
          'h-full overflow-y-scroll',
          isContentOverflowing ? 'pr-3' : 'no-scrollbar-desktop',
        ]"
      >
        <div
          :class="[
            'transition-all duration-300 w-full rounded-lg bg-white mt-12',
          ]"
        >
          <div
            v-if="!showResponse && selectedCandidates.length"
            class="flex flex-col items-center justify-center flex-wrap"
          >
            <h4 class="text-xl">Selected candidates:</h4>
            <div
              v-for="candidate in selectedCandidates"
              :key="candidate.id"
              class="flex flex-col items-center justify-center w-full"
            >
              <div
                :class="[
                  'mt-1 transition-all text-elections-primary-600 text-center w-[max-content] p-1 bg-elections-tertiary',
                ]"
              >
                {{ candidate.last_name }}, {{ candidate.first_name }}
              </div>
            </div>

            <div class="text-center mt-6 lg:mt-12">
              <p class="font-bold text-elections-primary-600">
                Are you sure you want to submit your vote for these candidates?
              </p>
              <p class="text-sm bg-red-200 font-semibold rounded-sm p-2">
                Submitting a vote is permanent and cannot be undone. You will
                not be able to change your votes after submitting.
              </p>

              <!-- Warning if not selecting maximum candidates -->
              <div
                v-if="isSelectingFewerThanMax && !confirmFewerCandidates"
                class="mt-5 p-6 bg-yellow-50 border-2 border-yellow-300 rounded-lg flex flex-col items-center justify-center gap-3"
              >
                <p
                  class="text-sm font-semibold text-yellow-800 flex gap-2 items-center mb-0"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="w-5 h-5"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  You are selecting {{ selectedCandidates.length }} of
                  {{ maxCandidates }} possible
                  {{ pluraliseNoun('candidate', maxCandidates.length) }}
                </p>
                <FormKit
                  v-model="confirmFewerCandidates"
                  type="checkbox"
                  decorator-icon="check"
                  validation="required"
                  validation-label="Confirmation"
                  label="I understand I am choosing fewer candidates than the maximum and wish to proceed."
                  label-class="text-xs text-gray-700 text-left"
                  outer-class="$remove:mb-4 mb-0"
                  @input="handleCheckboxChange"
                />
              </div>

              <!-- Countdown timer/submit button -->
              <template v-else>
                <p class="text-sm mt-8 mb-2">
                  Please take a few seconds to review before confirming.
                </p>

                <div
                  class="flex flex-col space-y-4 items-center justify-center"
                >
                  <button
                    type="button"
                    :class="[
                      'space-x-0 mt-4 mb-2',
                      countdown
                        ? 'bg-gray-400 text-white px-6 py-3 rounded-md cursor-not-allowed'
                        : 'brand__button--elections-primary',
                    ]"
                    :disabled="countdown"
                    @click.stop="vote"
                  >
                    <span v-if="countdown" class="flex items-center gap-2">
                      <span class="font-semibold text-lg">{{ countdown }}</span>
                      <span class="opacity-80">{{ confirmText() }}</span>
                    </span>
                    <span v-else>{{ confirmText() }}</span>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <div
            v-if="showResponse"
            class="text-center flex flex-col items-center justify-center gap-4"
          >
            <div
              v-if="responseRef.icon"
              :class="[
                'flex items-center justify-center text-white p-4 rounded-full',
                responseRef?.icon === 'success'
                  ? 'bg-elections-primary-300 text-secondary'
                  : 'bg-red-500 text-white',
              ]"
            >
              <svg
                v-if="responseRef.icon === 'success'"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-8 h-8"
              >
                <path
                  fill-rule="evenodd"
                  d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-if="responseRef.icon === 'failed'"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-8 h-8"
              >
                <path
                  fill-rule="evenodd"
                  d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <p
              :class="[
                responseRef?.icon === 'success'
                  ? 'text-primary'
                  : 'text-red-500',
                'text-center font-bold',
              ]"
            >
              <span v-if="responseRef?.icon === 'success'">Success!</span
              ><span v-else>Error!</span>
            </p>
            <p
              v-if="responseRef.text"
              class="text-center text-sm text-truegray-500"
            >
              {{ responseRef.text }}.
            </p>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import { defineComponent, ref, computed, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Modal from './Modal.vue'
import { isContainerContentOverflowing, pluraliseNoun } from '../../store/util'

export default defineComponent({
  name: 'ModalCandidate',

  components: {
    Modal,
  },

  props: {
    triggerClass: {
      type: String,
      default: 'brand__button--elections-primary',
    },
  },

  setup(props) {
    const store = useStore()
    const route = useRoute()

    const MODAL_CONFIG = {
      trigger: {
        text: 'Submit Vote',
        as: 'button',
        classes: props.triggerClass,
      },

      contentClasses:
        'max-h-[85vh] mobile-modal-width max-w-[750px] lg:overflow-y-auto p-8 lg:p-16 flex flex-col items-center justify-center',
    }

    const modalVotingContainerRef = ref(null)
    const isContentOverflowing = computed(() =>
      isContainerContentOverflowing(modalVotingContainerRef.value),
    )

    const selectedElection = computed(
      () => store.getters['elections/getCurrentElection'],
    )

    const canVote = computed(() => store.getters['elections/canVote'])

    const selectedCandidates = computed(
      () => store.getters['elections/getSelectedCandidates'],
    )

    const maxCandidates = computed(
      () => selectedElection.value?.min_candidates || 0,
    )

    const isSelectingFewerThanMax = computed(
      () => selectedCandidates.value.length < maxCandidates.value,
    )

    const confirmFewerCandidates = ref(false)

    const confirmText = () => {
      if (selectedCandidates.value.length > 1) {
        return 'Confirm Votes'
      } else {
        return 'Confirm Vote'
      }
    }

    const vote = async () => {
      const payload = {
        candidateIds: selectedCandidates.value.map((candidate) => candidate.id),
        electionId: selectedElection.value.id,
        votingToken: route.query.votingToken,
      }
      const response = await store.dispatch(
        'elections/dispatchAuthorisedVote',
        payload,
      )
      const responseData = response?.data
      if (responseData?.success) {
        responseRef.value.text =
          'Thank you. Your vote(s) have been successfully cast. You can now close this browser window'
        responseRef.value.icon = 'success'
      } else if (!responseData?.success) {
        if (responseData.alert) {
          responseRef.value.text = responseData.alert?.message
        } else if (responseData.timeout) {
          responseRef.value.text =
            'Invalid voting token. You have been timed out. Please try again later, or reach out to us if you feel this is in error'
        }
        responseRef.value.icon = 'failed'
      }

      showResponse.value = true
    }

    const showResponse = ref(false)
    const responseRef = ref({
      text: null,
      icon: null,
    })

    const countdown = ref(3)
    let countdownInterval = null
    let countdownTimeout = null

    const startCountdown = () => {
      // Clear any existing intervals/timeouts
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
      if (countdownTimeout) {
        clearTimeout(countdownTimeout)
        countdownTimeout = null
      }

      countdown.value = 3
      // 500 ms delay before starting the countdown to allow modal to fully open
      countdownTimeout = setTimeout(() => {
        countdownInterval = setInterval(() => {
          if (countdown.value) {
            countdown.value--
          } else {
            clearInterval(countdownInterval)
            countdownInterval = null
          }
        }, 1000)
      }, 500)
    }

    const handleCheckboxChange = () => {
      startCountdown()
    }

    const handleModalStateChange = (isOpen) => {
      if (isOpen) {
        // Only start countdown if not selecting fewer than max
        if (!isSelectingFewerThanMax.value) {
          startCountdown()
        }
        confirmFewerCandidates.value = false // Reset checkbox when modal opens
      } else {
        // Clean up when modal closes
        if (countdownInterval) {
          clearInterval(countdownInterval)
          countdownInterval = null
        }
        if (countdownTimeout) {
          clearTimeout(countdownTimeout)
          countdownTimeout = null
        }
      }
    }

    onUnmounted(() => {
      if (countdownInterval) {
        clearInterval(countdownInterval)
      }
      if (countdownTimeout) {
        clearTimeout(countdownTimeout)
      }
    })

    return {
      pluraliseNoun,
      MODAL_CONFIG,
      vote,
      modalVotingContainerRef,
      isContentOverflowing,
      selectedElection,
      canVote,
      showResponse,
      responseRef,
      selectedCandidates,
      confirmText,
      countdown,
      handleModalStateChange,
      handleCheckboxChange,
      maxCandidates,
      isSelectingFewerThanMax,
      confirmFewerCandidates,
    }
  },
})
</script>
